import React from 'react'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import { useTranslate } from 'src/localization/Trns'
import { useModalRoute } from 'src/navigation/modalParams'
import { g2ReviewOverlayRoute } from 'src/navigation/overlayRoutes'

import style from './G2ReviewBox.style'

/**
 * The G2 review modal is URL-driven (`?modal=g2Review`, deep-linkable from
 * outside the app as `/profile/info/review` — see `overlayRoutes.ts`), so this
 * box only owns the trigger; the modal itself is rendered once in `Authed.tsx`.
 *
 * The CTA is a real link, so it is shareable, middle-clickable, and
 * `role="link"` for e2e. It targets `getOpenLocation`, which keeps the CURRENT
 * pathname — the overlay opens in place and closing returns the user to the
 * page they were on. That is the pattern for every in-app trigger, including a
 * future home-page CTA: linking to the `/profile/info/review` deep link instead
 * would navigate the user into profile settings and leave them there on close.
 *
 * A second trigger under the routing boundary (`src/feature/`) cannot call the
 * hook itself; its page computes this location and passes it down as a prop.
 */
const G2ReviewBox: React.FC = () => {
  const modal = useModalRoute()
  const translate = useTranslate()

  return (
    <Box css={style} className="G2ReviewBox" data-testid="g2-review-box">
      <span className="G2ReviewBox-title">
        {translate('A little review goes a long way')}
      </span>
      <span className="G2ReviewBox-description">
        {translate('Share your experience with others on G2!')}
      </span>
      <div className="G2ReviewBox-button">
        <Button
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          text={translate('Leave a review')}
          linkProps={{
            to: {
              ...modal.getOpenLocation(g2ReviewOverlayRoute.name),
              hash: '',
              state: {},
            },
            ariaLabelSource: 'Leave a review',
          }}
          big
        />
      </div>
    </Box>
  )
}

export default G2ReviewBox
